// ─────────────────────────────────────────────────────────────────────────────
// 🔑 G10 (Jesse, 2026-10-16): the explicit, audited staff action that reopens a borrower's completed guided flow.
// Nothing else reopens it (approval status never does). A reason is required; the server records it in the
// audit log with the prior completion, alerts staff, and lands the borrower on the lower of their saved step
// and the step the gate computes (POST /api/admin/borrowers/:id/guided-flow/reopen).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function GuidedFlowReopenCard({ borrowerId, token }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/borrowers/${borrowerId}/guided-flow/reopen`, { reason },
        { headers: { Authorization: `Bearer ${token}` } });
      setResult(`Reopened. The borrower re-enters at step ${r.data.step}.`);
      setOpen(false); setReason('');
    } catch (e) {
      setResult(e?.response?.data?.message || e?.response?.data?.error || 'Could not reopen.');
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-guided-flow-reopen" className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <div className="text-sm font-semibold text-gray-800 mb-1">Application</div>
      <div className="text-xs text-gray-500 mb-2">
        A completed application stays closed. Reopen it only when the borrower must redo a step; the reason is recorded.
      </div>
      {open ? (
        <div>
          <textarea data-testid="admin-guided-flow-reopen-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            rows={3} className="w-full border border-gray-300 rounded-lg p-2 text-sm" placeholder="Why the application is being reopened" />
          <div className="flex gap-2 mt-2">
            <button type="button" data-testid="admin-guided-flow-reopen-confirm" disabled={busy || reason.trim().length < 10} onClick={submit}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-[#3a6ad9] disabled:bg-gray-300">Reopen</button>
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 rounded-lg text-sm border border-gray-300">Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" data-testid="admin-guided-flow-reopen-button" onClick={() => setOpen(true)}
          className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-800">Reopen application…</button>
      )}
      {result && <div className="text-xs text-gray-700 mt-2">{result}</div>}
    </div>
  );
}
